// Auth redirect targets have two wire shapes (contracts 05 §11 and 06 §8):
// - `?next=` is a guard-authored internal fullPath (`/summary?range=7d`).
// - `redirectTarget` and the fallback are route names (`Summary`).
// Keep this framework-light while preserving the distinction Vue Router needs.
export type PostLoginLocation = string | { name: string }

/** Where an unsafe target lands instead. A route NAME, so it cannot itself carry a host. */
const SAFE_FALLBACK: PostLoginLocation = { name: 'Summary' }

/**
 * Strip what a URL parser ignores before it resolves. `\t`, `\n` and `\r` are dropped outright by
 * the WHATWG parser and leading/trailing C0 + space are trimmed, so `"/\t/evil.example"` is really
 * `"//evil.example"` by the time the browser sees it — judge the stripped form.
 *
 * By code point rather than by regex: a character class of C0 controls is exactly what
 * `no-control-regex` forbids.
 */
function stripUrlIgnored(target: string): string {
  return [...target]
    .filter((character) => {
      const code = character.codePointAt(0) ?? 0
      return code > 0x20 && code !== 0x7f
    })
    .join('')
}

/** A route NAME is an identifier, never a URL. Anything else is not a name we authored. */
const ROUTE_NAME = /^[A-Za-z][\dA-Za-z]*$/u

/**
 * Is this a target that stays on this origin?
 *
 * `startsWith('/')` alone is NOT an internal-path test — it also accepts the classic
 * open-redirect forms, all of which a browser resolves against a FOREIGN host:
 *   • protocol-relative `//evil.example`;
 *   • the backslash variants `/\evil.example` and `\\evil.example`, which browsers normalise to
 *     the same thing;
 *   • the same three again behind a stripped control character (`/\t/evil.example`).
 *
 * The value reaches this function from `?next` on the login URL and — through `redirectTarget`,
 * which `authStore` persists via `secure-ls` — survives a reload and the MFA hop, so a poisoned
 * value outlives the tab that received it. Both call sites funnel through here; validate once.
 */
function isSafeInternalPath(target: string): boolean {
  const stripped = stripUrlIgnored(target)
  if (!stripped.startsWith('/')) return false
  return !stripped.startsWith('//') && !stripped.startsWith('/\\')
}

export function toPostLoginLocation(target: string): PostLoginLocation {
  const stripped = stripUrlIgnored(target)
  if (!stripped) return SAFE_FALLBACK
  if (stripped.startsWith('/') || stripped.startsWith('\\')) {
    return isSafeInternalPath(target) ? target : SAFE_FALLBACK
  }
  // Not a path ⇒ it must be a route name. `https://evil.example` and `javascript:alert(1)` fail
  // this test, so an attacker-supplied absolute URL can never become a location at all.
  return ROUTE_NAME.test(stripped) ? { name: stripped } : SAFE_FALLBACK
}
